import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ImagePlus, Save } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Loading, StatRow } from "@/components/Common";
import { EUR, EURc, num, pnlColor, fmtTime, dur } from "@/lib/format";
import api from "@/lib/api";

const SETUPS = ["Breakout", "Pullback", "Reversal", "Continuation", "Liquidity Sweep", "Support/Resistance", "Other"];
const EMOTIONS = ["Calm", "Confident", "Fearful", "Greedy", "Neutral", "Impatient"];

export default function TradeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: t, isLoading, refetch } = useQuery({
    queryKey: ["trade", id],
    queryFn: () => api.get(`/trades/${id}`).then((r) => r.data),
  });
  const [journal, setJournal] = useState({ setup: "", notes: "", emotion: "", confidence: 3, mistake: "", lesson: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (t) setJournal({
      setup: t.setup || "", notes: t.notes || "", emotion: t.emotion || "",
      confidence: t.confidence || 3, mistake: t.mistake || "", lesson: t.lesson || "",
    });
  }, [t]);

  if (isLoading || !t) return <Loading />;
  const a = t.analysis;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/trades/${id}/journal`, journal);
      toast.success("Diario salvato");
      refetch();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const Info = ({ label, value, tone }) => (
    <div>
      <div className="font-sans text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-1 font-mono text-sm ${tone === "profit" ? "text-profit" : tone === "loss" ? "text-loss" : "text-white"}`}>{value}</div>
    </div>
  );

  return (
    <div data-testid="trade-detail-page">
      <button onClick={() => navigate("/trades")} data-testid="back-to-trades"
        className="mb-5 flex items-center gap-2 font-sans text-xs text-zinc-400 hover:text-gold">
        <ArrowLeft size={14} /> Torna ai Trades
      </button>

      <PageHeader title={`${t.symbol} · #${t.ticket}`} subtitle={`${t.direction} · ${t.session} · ${fmtTime(t.open_time)}`}>
        <span className={`rounded-full border px-4 py-1.5 font-mono text-lg font-bold ${pnlColor(t.net_profit)}`}
          style={{ borderColor: t.net_profit >= 0 ? "rgba(16,185,129,.3)" : "rgba(239,68,68,.3)" }}>
          {EURc(t.net_profit)}
        </span>
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="dlx-card p-5 sm:p-6">
          <div className="mb-4 font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Informazioni</div>
          <div className="grid grid-cols-2 gap-4">
            <Info label="Volume" value={t.volume} />
            <Info label="Direzione" value={t.direction} tone={t.direction === "BUY" ? "profit" : "loss"} />
            <Info label="Entry" value={num(t.entry_price, 2)} />
            <Info label="Exit" value={num(t.exit_price, 2)} />
            <Info label="Stop Loss" value={num(t.stop_loss, 2)} />
            <Info label="Take Profit" value={num(t.take_profit, 2)} />
            <Info label="Open" value={fmtTime(t.open_time)} />
            <Info label="Close" value={fmtTime(t.close_time)} />
            <Info label="Durata" value={dur(t.duration_seconds)} />
            <Info label="Magic" value={t.magic} />
          </div>
        </div>

        <div className="dlx-card p-5 sm:p-6">
          <div className="mb-4 font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Analisi</div>
          <StatRow label="Gross P&L" value={EURc(t.gross_profit)} tone={t.gross_profit >= 0 ? "profit" : "loss"} />
          <StatRow label="Commission" value={EUR(t.commission)} />
          <StatRow label="Swap" value={EUR(t.swap)} />
          <StatRow label="Net P&L" value={EURc(t.net_profit)} tone={t.net_profit >= 0 ? "profit" : "loss"} />
          <StatRow label="R Multiple" value={a.r_multiple?.toFixed(2)} tone={a.r_multiple >= 0 ? "profit" : "loss"} />
          <StatRow label="Risk" value={EUR(a.risk)} />
          <StatRow label="Risk / Reward" value={a.risk_reward.toFixed(2)} />
          <StatRow label="MFE" value={EUR(a.mfe)} />
          <StatRow label="MAE" value={EUR(a.mae)} />
        </div>

        <div className="dlx-card p-5 sm:p-6">
          <div className="mb-4 font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Contesto</div>
          <StatRow label="Giorno" value={t.day_of_week} />
          <StatRow label="Ora" value={`${t.hour_of_day}:00`} />
          <StatRow label="Sessione" value={t.session} />
          <StatRow label="Setup" value={t.setup} />
          <StatRow label="Risultato" value={t.result} tone={t.result === "WIN" ? "profit" : "loss"} />
          <div className="mt-4 flex items-center justify-center gap-2 rounded-lg border border-dashed border-white/15 py-6 text-zinc-600">
            <ImagePlus size={16} /><span className="font-sans text-xs">Screenshot (in arrivo)</span>
          </div>
        </div>
      </div>

      {/* Journal */}
      <div className="dlx-card mt-6 p-5 sm:p-6" data-testid="trade-journal">
        <div className="mb-5 font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Diario del Trade</div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block font-sans text-[11px] uppercase tracking-widest text-zinc-500">Setup</label>
            <select value={journal.setup} onChange={(e) => setJournal({ ...journal, setup: e.target.value })} data-testid="journal-setup"
              className="w-full rounded-lg border border-white/10 bg-[#141418] px-3 py-2.5 font-sans text-sm text-white outline-none focus:border-gold/40">
              <option value="">—</option>{SETUPS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block font-sans text-[11px] uppercase tracking-widest text-zinc-500">Emozione</label>
            <select value={journal.emotion} onChange={(e) => setJournal({ ...journal, emotion: e.target.value })} data-testid="journal-emotion"
              className="w-full rounded-lg border border-white/10 bg-[#141418] px-3 py-2.5 font-sans text-sm text-white outline-none focus:border-gold/40">
              <option value="">—</option>{EMOTIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1.5 block font-sans text-[11px] uppercase tracking-widest text-zinc-500">Livello di sicurezza: {journal.confidence}/5</label>
            <input type="range" min="1" max="5" value={journal.confidence} data-testid="journal-confidence"
              onChange={(e) => setJournal({ ...journal, confidence: Number(e.target.value) })}
              className="w-full accent-[#D4AF37]" />
          </div>
          <div>
            <label className="mb-1.5 block font-sans text-[11px] uppercase tracking-widest text-zinc-500">Note</label>
            <textarea value={journal.notes} onChange={(e) => setJournal({ ...journal, notes: e.target.value })} rows={3} data-testid="journal-notes"
              className="w-full rounded-lg border border-white/10 bg-[#141418] px-3 py-2.5 font-sans text-sm text-white outline-none focus:border-gold/40" />
          </div>
          <div>
            <label className="mb-1.5 block font-sans text-[11px] uppercase tracking-widest text-zinc-500">Errore commesso</label>
            <textarea value={journal.mistake} onChange={(e) => setJournal({ ...journal, mistake: e.target.value })} rows={3} data-testid="journal-mistake"
              className="w-full rounded-lg border border-white/10 bg-[#141418] px-3 py-2.5 font-sans text-sm text-white outline-none focus:border-gold/40" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1.5 block font-sans text-[11px] uppercase tracking-widest text-zinc-500">Lezione imparata</label>
            <textarea value={journal.lesson} onChange={(e) => setJournal({ ...journal, lesson: e.target.value })} rows={2} data-testid="journal-lesson"
              className="w-full rounded-lg border border-white/10 bg-[#141418] px-3 py-2.5 font-sans text-sm text-white outline-none focus:border-gold/40" />
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="journal-save"
          className="mt-5 flex items-center gap-2 rounded-lg px-5 py-2.5 font-sans text-sm font-semibold text-black transition-all hover:brightness-110 active:scale-95 disabled:opacity-60"
          style={{ background: "linear-gradient(135deg,#BF953F,#FCF6BA 45%,#AA771C)" }}>
          <Save size={15} /> {saving ? "Salvataggio…" : "Salva Diario"}
        </button>
      </div>
    </div>
  );
}
